import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuditLogIcon, AuditLogSentence, actorLabel } from './AuditLogSummary';
import { auditLogQuery } from './query';
import { KoobPlaceholder } from '@koob/ui';

const formatDateTime = (value) =>
  new Date(value).toLocaleString('en', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * The latest events of one resource, for an overview: who did what, when,
 * and a link to its full audit log. `fetchResourceAuditLogs(type, id, query)`
 * resolves to `{ items, total }`.
 */
export default function AuditLogRecent({
  type,
  id,
  fetchResourceAuditLogs,
  seeAllTo,
  limit = 5,
}) {
  const { t } = useTranslation('auditLog');
  const [items, setItems] = useState();

  useEffect(() => {
    let cancelled = false;
    fetchResourceAuditLogs(
      type,
      id,
      auditLogQuery(new URLSearchParams(), limit, []),
    )
      .then((data) => !cancelled && setItems(data?.items ?? []))
      .catch(() => !cancelled && setItems([]));
    return () => {
      cancelled = true;
    };
  }, [fetchResourceAuditLogs, type, id, limit]);

  if (!items) {
    return <KoobPlaceholder className="h-24 rounded" />;
  }
  if (!items.length) {
    return <p className="m-0 text-sm text-gray-500">{t('empty')}</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      <ul className="m-0 flex list-none flex-col gap-3 p-0">
        {items.map((row) => (
          <li key={row.id} className="flex items-start gap-3">
            <AuditLogIcon row={row} />
            <div className="min-w-0">
              <AuditLogSentence row={row} />
              <div className="text-xs text-gray-500">
                {actorLabel(t, row)} ·{' '}
                <time dateTime={row.occurredAt}>
                  {formatDateTime(row.occurredAt)}
                </time>
              </div>
            </div>
          </li>
        ))}
      </ul>
      {seeAllTo && (
        <Link
          to={seeAllTo}
          className="self-start text-sm font-medium text-orange-600 hover:underline"
        >
          {t('seeAll')}
        </Link>
      )}
    </div>
  );
}
